/** client/src/components/DocumentCommentPanel.js **/

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const FIELD_LABELS = {
  title: 'Title',
  summary: 'Summary',
  project_based_summary: 'Dossier Summary',
  author: 'Author',
  organization: 'Organization',
  categories: 'Categories',
};

export default function DocumentCommentPanel({ documentId, onClose, onNotesChange }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState({});
  const [commenting, setCommenting] = useState({});

  useEffect(() => {
    if (documentId) loadNotes();
  }, [documentId]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notes`, {
        params: { entity_type: 'document', entity_id: documentId },
        withCredentials: true,
      });
      setNotes(res.data);
      onNotesChange?.(res.data.length);
    } catch (err) {
      console.error('Failed to load notes', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API_URL}/api/notes/${noteId}`, { withCredentials: true });
      const updated = notes.filter(n => n.id !== noteId);
      setNotes(updated);
      onNotesChange?.(updated.length);
    } catch (err) {
      console.error('Failed to delete note', err);
    }
  };

  const submitComment = async (noteId) => {
    const body = newComment[noteId]?.trim();
    if (!body) return;
    setCommenting(prev => ({ ...prev, [noteId]: true }));
    try {
      const res = await axios.post(`${API_URL}/api/notes/${noteId}/comments`, { body }, { withCredentials: true });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, comments: res.data } : n));
      setNewComment(prev => ({ ...prev, [noteId]: '' }));
    } catch (err) {
      console.error('Failed to add comment', err);
    } finally {
      setCommenting(prev => ({ ...prev, [noteId]: false }));
    }
  };

  // Group notes by field_key
  const grouped = notes.reduce((acc, note) => {
    const key = note.field_key || 'general';
    if (!acc[key]) acc[key] = [];
    acc[key].push(note);
    return acc;
  }, {});

  return (
    <div className="w-72 flex-shrink-0 border-l bg-white flex flex-col h-full overflow-hidden">
      <div className="flex justify-between items-center px-4 py-3 border-b">
        <span className="text-sm font-semibold text-gray-700">All Comments</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4 text-sm">
        {loading && <p className="text-xs text-gray-400">Loading…</p>}
        {!loading && notes.length === 0 && (
          <p className="text-xs text-gray-400 text-center mt-8">No comments yet.<br />Click the comment icon next to any field to add one.</p>
        )}

        {Object.entries(grouped).map(([fieldKey, fieldNotes]) => (
          <div key={fieldKey}>
            <div className="text-[10px] font-semibold uppercase text-gray-400 mb-1 tracking-wide">
              {FIELD_LABELS[fieldKey] || fieldKey}
            </div>
            <div className="space-y-2">
              {fieldNotes.map(note => (
                <div key={note.id} className="border rounded p-2 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <p className="text-xs text-gray-800 flex-1">{note.note}</p>
                    <button
                      onClick={() => deleteNote(note.id)}
                      className="text-gray-300 hover:text-red-500 ml-2 text-xs flex-shrink-0"
                      title="Delete note"
                    >✕</button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {note.author_email} · {new Date(note.created_at).toLocaleString()}
                  </p>

                  {note.comments?.length > 0 && (
                    <div className="mt-2 space-y-1 pl-2 border-l-2 border-blue-200">
                      {note.comments.map(c => (
                        <div key={c.id} className={`text-xs p-1 rounded ${c.author_type === 'ai' ? 'bg-blue-50 text-blue-800' : 'bg-white text-gray-700'}`}>
                          <span className="font-semibold text-[10px] mr-1">
                            {c.author_type === 'ai' ? '🤖 Atlas AI' : `👤 ${c.author_label}`}
                          </span>
                          {c.body}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-1 mt-2">
                    <input
                      type="text"
                      placeholder="Comment…"
                      value={newComment[note.id] || ''}
                      onChange={e => setNewComment(prev => ({ ...prev, [note.id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitComment(note.id); } }}
                      className="flex-1 border rounded px-2 py-0.5 text-[11px]"
                    />
                    <button
                      disabled={commenting[note.id]}
                      onClick={() => submitComment(note.id)}
                      className="bg-gray-200 hover:bg-gray-300 px-2 py-0.5 rounded text-[11px] disabled:opacity-50"
                    >
                      {commenting[note.id] ? '…' : 'Send'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
